import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Wrench, ClipboardText, Hammer, Pulse, CheckCircle, WarningCircle, Siren, Timer, ShieldCheck, Gauge, MapPin, SpinnerGap } from "@phosphor-icons/react";
import { fmtD, titleCase, iconFor } from "../components/assets/assetUtils";
import { AssetHealthBadge } from "../components/assets/AssetHealthBadge";

const LOG_ICON = { service: Wrench, inspection: ClipboardText, repair: Hammer, condition: Pulse };
const SEVERITIES = [["low", "Minor", "Cosmetic or not urgent"], ["medium", "Needs attention", "Working but not right"], ["high", "Urgent", "Not working / safety risk"]];

export default function AssetPassportPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", severity: "medium", description: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    API.get(`/public/asset/${code}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.status === 404 ? "This tag isn't registered to any asset." : "Couldn't load this asset right now."));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data: res } = await API.post(`/public/asset/${code}/report`, form);
      setDone(res.reference);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't send the report"); } finally { setSending(false); }
  };

  const b = data?.branding || {};
  const accent = b.primary_color || "#1D4ED8";
  const logo = b.logo_url ? (b.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${b.logo_url}` : b.logo_url) : "";

  if (error && !data) return <Shell accent={accent}><div className="text-center py-20 px-6" data-testid="passport-error"><WarningCircle size={40} className="mx-auto mb-3 text-amber-500" /><p className="font-semibold">{error}</p></div></Shell>;
  if (!data) return <Shell accent={accent}><div className="flex justify-center py-24"><SpinnerGap size={28} className="animate-spin text-muted-foreground" /></div></Shell>;

  const a = data.asset;
  const Icon = iconFor(a.category);
  const tone = a.service_state === "overdue" ? "bg-red-50 text-red-700 border-red-200" : a.service_state === "due_soon" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200";

  return (
    <Shell accent={accent} logo={logo} company={b.company_name} footer={b.footer_text} isPlatform={b.is_platform}>
      <div className="px-5 pt-6 pb-4" data-testid="passport-header">
        <div className="flex items-start gap-3">
          <span className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 text-white" style={{ background: accent }}><Icon size={24} weight="bold" /></span>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{a.category}{a.tag_code ? ` · ${a.tag_code}` : ""}</p>
            <h1 className="text-2xl font-bold leading-tight" data-testid="passport-name">{a.name}</h1>
            {(a.property_name || a.location) && <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={13} /> {[a.property_name, a.location].filter(Boolean).join(" · ")}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          <Chip>{titleCase(a.status)}</Chip><Chip>Condition: {a.condition}</Chip>{a.criticality !== "medium" && <Chip>{a.criticality} criticality</Chip>}
          <AssetHealthBadge health={data.health} className="rounded-full" testid="passport-health" />
          {data.open_faults > 0 && <Chip className="border-red-200 bg-red-50 text-red-700" testid="passport-open-faults"><Siren size={11} className="inline mr-1" />{data.open_faults} fault{data.open_faults === 1 ? "" : "s"} already reported</Chip>}
        </div>
      </div>

      <div className="px-5 grid grid-cols-2 gap-3" data-testid="passport-service">
        <div className={`rounded-xl border p-3 ${tone}`}>
          <p className="text-[10px] uppercase tracking-wider flex items-center gap-1 opacity-80"><Timer size={11} /> Next service</p>
          <p className="font-semibold text-sm mt-1">{a.next_service_due ? fmtD(a.next_service_due) : "Not scheduled"}</p>
          {a.service_state === "overdue" && <p className="text-[11px]">{Math.abs(a.days_to_service)} days overdue</p>}
          {a.service_state === "due_soon" && <p className="text-[11px]">in {a.days_to_service} days</p>}
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] uppercase tracking-wider flex items-center gap-1 text-muted-foreground"><CheckCircle size={11} /> Last serviced</p>
          <p className="font-semibold text-sm mt-1">{fmtD(a.last_serviced_at)}</p>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><ShieldCheck size={11} /> Warranty {a.warranty_state === "none" ? "n/a" : a.warranty_state}</p>
        </div>
      </div>

      <div className="px-5 mt-4">
        <div className="rounded-xl border border-border divide-y divide-border text-sm">
          <Row k="Manufacturer" v={a.manufacturer} /><Row k="Model" v={a.model} /><Row k="Serial" v={a.serial_number} mono /><Row k="Installed" v={a.install_date ? fmtD(a.install_date) : ""} />
          {a.meter_unit && <Row k="Meter" v={a.meter_reading != null ? `${Number(a.meter_reading).toLocaleString()} ${a.meter_unit}` : ""} icon={Gauge} />}
          {a.parent_name && <Row k="Part of" v={a.parent_name} />}
        </div>
      </div>

      <div className="px-5 mt-5" data-testid="passport-cta">
        {done ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800" data-testid="passport-report-done">
            <p className="font-semibold flex items-center gap-2"><CheckCircle size={18} weight="fill" /> Fault reported — thank you</p>
            <p className="text-sm mt-1">Reference <span className="font-mono font-semibold">{done}</span>. The maintenance team has been notified{form.phone ? " and will call you if they need more detail" : ""}.</p>
          </div>
        ) : !reportOpen ? (
          <Button className="w-full h-12 text-base text-white" style={{ background: accent }} onClick={() => setReportOpen(true)} data-testid="passport-report-btn"><Siren size={18} className="mr-2" /> Report a fault with this asset</Button>
        ) : (
          <form onSubmit={submit} className="rounded-xl border border-border p-4 space-y-3" data-testid="passport-report-form">
            <p className="font-semibold text-sm">What's wrong?</p>
            <div className="grid grid-cols-3 gap-2">
              {SEVERITIES.map(([v, l, d]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, severity: v })} className={`rounded-lg border p-2 text-left ${form.severity === v ? "border-foreground bg-muted" : "border-border"}`} data-testid={`passport-severity-${v}`}>
                  <p className="text-xs font-semibold">{l}</p><p className="text-[10px] text-muted-foreground leading-tight">{d}</p>
                </button>
              ))}
            </div>
            <textarea required minLength={5} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Describe the fault — noises, leaks, error codes, when it started…" data-testid="passport-description" />
            <div className="grid grid-cols-2 gap-2">
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *" data-testid="passport-reporter-name" />
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" inputMode="tel" data-testid="passport-reporter-phone" />
              <Input className="col-span-2" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (for updates)" data-testid="passport-reporter-email" />
            </div>
            <div className="flex gap-2"><Button type="button" variant="outline" className="flex-1" onClick={() => setReportOpen(false)}>Cancel</Button><Button type="submit" className="flex-1 text-white" style={{ background: accent }} disabled={sending} data-testid="passport-report-submit">{sending ? "Sending…" : "Send report"}</Button></div>
          </form>
        )}
      </div>

      <div className="px-5 mt-6 pb-10" data-testid="passport-history">
        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Service history</p>
        {a.history.length === 0 ? <p className="text-sm text-muted-foreground">No services recorded yet.</p> : (
          <ol className="relative border-l border-border ml-2 space-y-3">
            {a.history.map((h, i) => { const I = LOG_ICON[h.type] || Wrench; return (
              <li key={i} className="ml-4">
                <span className="absolute -left-2.5 h-5 w-5 rounded-full bg-background border border-border flex items-center justify-center"><I size={11} /></span>
                <p className="text-sm font-medium leading-tight">{h.summary || titleCase(h.type)}</p>
                <p className="text-[11px] text-muted-foreground">{titleCase(h.type)} · {fmtD(h.date)}{h.performed_by ? ` · ${h.performed_by}` : ""}</p>
              </li>
            ); })}
          </ol>
        )}
      </div>
    </Shell>
  );
}

function Shell({ accent, logo, company, footer, isPlatform, children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="h-1.5" style={{ background: accent }} />
      <div className="max-w-md mx-auto">
        <header className="px-5 py-4 flex items-center gap-3 border-b border-border" data-testid="passport-brand">
          {logo ? <img src={logo} alt={company || "Logo"} className="h-8 max-w-[140px] object-contain" /> : <span className="font-bold text-lg">{company || "Asset passport"}</span>}
          <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Asset passport</span>
        </header>
        {children}
        <footer className="px-5 py-4 text-center text-[11px] text-muted-foreground border-t border-border">{footer || (isPlatform ? "" : `Maintained by ${company || "our team"}`)}</footer>
      </div>
    </div>
  );
}
const Chip = ({ children, className = "", testid }) => <span className={`inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[11px] capitalize ${className}`} data-testid={testid}>{children}</span>;
const Row = ({ k, v, mono, icon: I }) => v ? <div className="flex items-center justify-between px-3 py-2"><span className="text-muted-foreground flex items-center gap-1">{I && <I size={13} />} {k}</span><span className={mono ? "font-mono" : "font-medium"}>{v}</span></div> : null;
